// Mobile Transfer account block (§6.1): holder name, sub-type dropdown and the
// per-sub-type fields, with the Verify flow and §17.5 mobile wording.
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { verifyMobile, verifyUpiId } from '@/api/beneficiaries';
import { getBillCategories, getBillers, getCircles, getOperators } from '@/api/reference';
import { ApiError } from '@/api/client';
import { isValidMobile, isValidUpiId } from '@/utils/phase2Validation';
import type { MobileSubType } from '@/types/phase2.types';
import PickerModal from './PickerModal';
import { emptyMobileBlock, type MobileBlock } from './formModel';

interface MobileTransferFieldsProps {
  block: MobileBlock;
  showErrors: boolean;
  onChange: (block: MobileBlock) => void;
}

const SUB_TYPES: Array<{ value: MobileSubType; label: string }> = [
  { value: 'UPI_ID', label: 'UPI ID' },
  { value: 'MOBILE_NUMBER', label: 'Mobile number' },
  { value: 'MERCHANT_UPI', label: 'Merchant UPI' },
  { value: 'MOBILE_RECHARGE', label: 'Mobile recharge' },
  { value: 'BILL_PAYMENT', label: 'Bill payment' },
];

const MobileTransferFields = ({ block, showErrors, onChange }: MobileTransferFieldsProps) => {
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [operators, setOperators] = useState<string[]>([]);
  const [circles, setCircles] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [billers, setBillers] = useState<string[]>([]);
  const [billerPickerOpen, setBillerPickerOpen] = useState(false);

  useEffect(() => {
    if (block.subType === 'MOBILE_RECHARGE') {
      getOperators().then(setOperators).catch(() => setOperators([]));
      getCircles().then(setCircles).catch(() => setCircles([]));
    }
    if (block.subType === 'BILL_PAYMENT') {
      getBillCategories().then(setCategories).catch(() => setCategories([]));
    }
  }, [block.subType]);

  useEffect(() => {
    if (block.subType === 'BILL_PAYMENT' && block.billCategory) {
      getBillers(block.billCategory).then(setBillers).catch(() => setBillers([]));
    } else {
      setBillers([]);
    }
  }, [block.subType, block.billCategory]);

  const set = (patch: Partial<MobileBlock>) => onChange({ ...block, ...patch });

  const changeSubType = (subType: MobileSubType | '') => {
    // Changing the type clears the block (§6.1).
    onChange({ ...emptyMobileBlock(), key: block.key, subType });
    setVerifyError('');
  };

  const runVerify = async () => {
    setVerifying(true);
    setVerifyError('');
    try {
      if (block.subType === 'UPI_ID') {
        const result = await verifyUpiId(block.upiId);
        if (result.outcome === 'NOT_FOUND') {
          set({ verified: 'NONE', holderName: '' });
          setVerifyError("We couldn't find this UPI ID.");
        } else {
          set({ verified: result.outcome === 'FLAGGED' ? 'FLAGGED' : 'FOUND', holderName: result.holderName ?? '' });
        }
      } else if (block.subType === 'MOBILE_NUMBER') {
        const result = await verifyMobile(block.mobile);
        if (result.outcome === 'NOT_FOUND') {
          set({ verified: 'NONE', holderName: '' });
          // §17.5 — mobile wording, not "UPI ID".
          setVerifyError("We couldn't find a UPI account on this mobile number.");
        } else {
          set({ verified: result.outcome === 'FLAGGED' ? 'FLAGGED' : 'FOUND', holderName: result.holderName ?? '' });
        }
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Verification failed. Try again.');
    } finally {
      setVerifying(false);
    }
  };

  const holderReadOnly =
    (block.subType === 'UPI_ID' || block.subType === 'MOBILE_NUMBER') && block.verified !== 'NONE';
  const holderHidden = block.subType === 'MERCHANT_UPI';
  const holderTyped = block.subType === 'MOBILE_RECHARGE' || block.subType === 'BILL_PAYMENT';

  return (
    <div className="nova-block-fields">
      {!holderHidden && (
        <div className="mb-3">
          <label className="form-label">Holder name</label>
          <input
            type="text"
            className={`form-control ${showErrors && !block.holderName.trim() && (holderTyped || block.verified !== 'NONE') ? 'is-invalid' : ''}`}
            value={block.holderName}
            readOnly={holderReadOnly || (!holderTyped && block.subType !== '' )}
            placeholder={block.subType === 'UPI_ID' || block.subType === 'MOBILE_NUMBER' ? 'Filled by verification' : 'Holder name'}
            onChange={(e) => holderTyped && set({ holderName: e.target.value })}
          />
          {block.verified === 'FOUND' && (
            <div className="nova-field-ok">
              <FontAwesomeIcon icon={faCircleCheck} /> Verified
            </div>
          )}
          {showErrors && holderTyped && !block.holderName.trim() && (
            <div className="invalid-feedback d-block">Required</div>
          )}
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">Mobile transfer type</label>
        <select
          className="form-select"
          value={block.subType}
          onChange={(e) => changeSubType(e.target.value as MobileSubType | '')}
        >
          <option value="">-- Select a type --</option>
          {SUB_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {(block.subType === 'UPI_ID' || block.subType === 'MERCHANT_UPI') && (
        <>
          <div className="mb-3">
            <label className="form-label">{block.subType === 'MERCHANT_UPI' ? 'Merchant UPI ID' : 'UPI ID'}</label>
            <div className="nova-field-row">
              <input
                type="text"
                className={`form-control ${showErrors && !isValidUpiId(block.upiId) ? 'is-invalid' : ''}`}
                placeholder="name@bank"
                value={block.upiId}
                onChange={(e) => {
                  // Editing the UPI ID resets verification and clears the name (§6.1).
                  set({ upiId: e.target.value, verified: 'NONE', holderName: '' });
                  setVerifyError('');
                }}
              />
              {block.subType === 'UPI_ID' && (
                <button
                  type="button"
                  className="btn btn-outline-primary nova-verify-btn"
                  disabled={!isValidUpiId(block.upiId) || verifying}
                  onClick={() => void runVerify()}
                >
                  {verifying ? 'Verifying…' : 'Verify'}
                </button>
              )}
            </div>
            {showErrors && !isValidUpiId(block.upiId) && (
              <div className="invalid-feedback d-block">Enter a valid UPI ID, like name@bank.</div>
            )}
            {verifyError && <div className="invalid-feedback d-block">{verifyError}</div>}
            {showErrors && block.subType === 'UPI_ID' && block.verified === 'NONE' && isValidUpiId(block.upiId) && !verifyError && (
              <div className="invalid-feedback d-block">Verify the UPI ID first.</div>
            )}
          </div>
          {block.subType === 'MERCHANT_UPI' && (
            <div className="mb-3">
              <label className="form-label">Merchant name (optional)</label>
              <input
                type="text"
                className="form-control"
                value={block.merchantName}
                onChange={(e) => set({ merchantName: e.target.value })}
              />
            </div>
          )}
        </>
      )}

      {block.subType === 'MOBILE_NUMBER' && (
        <div className="mb-3">
          <label className="form-label">Mobile number</label>
          <div className="nova-field-row">
            <div className="input-group">
              <span className="input-group-text">+91</span>
              <input
                type="text"
                inputMode="numeric"
                className={`form-control ${showErrors && !isValidMobile(block.mobile) ? 'is-invalid' : ''}`}
                value={block.mobile}
                maxLength={10}
                onChange={(e) => {
                  set({ mobile: e.target.value.replace(/\D/g, '').slice(0, 10), verified: 'NONE', holderName: '' });
                  setVerifyError('');
                }}
              />
            </div>
            <button
              type="button"
              className="btn btn-outline-primary nova-verify-btn"
              disabled={!isValidMobile(block.mobile) || verifying}
              onClick={() => void runVerify()}
            >
              {verifying ? 'Verifying…' : 'Verify'}
            </button>
          </div>
          {showErrors && !isValidMobile(block.mobile) && (
            <div className="invalid-feedback d-block">Enter a valid 10-digit mobile number.</div>
          )}
          {verifyError && <div className="invalid-feedback d-block">{verifyError}</div>}
          {showErrors && block.verified === 'NONE' && isValidMobile(block.mobile) && !verifyError && (
            <div className="invalid-feedback d-block">Verify the mobile number first.</div>
          )}
        </div>
      )}

      {block.verified === 'FLAGGED' && (
        <div className="nova-flag-box" role="alert">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          This UPI ID has been reported. Check with the person before you pay.
        </div>
      )}

      {block.subType === 'MOBILE_RECHARGE' && (
        <>
          <div className="mb-3">
            <label className="form-label">Mobile number</label>
            <div className="input-group">
              <span className="input-group-text">+91</span>
              <input
                type="text"
                inputMode="numeric"
                className={`form-control ${showErrors && !isValidMobile(block.mobile) ? 'is-invalid' : ''}`}
                value={block.mobile}
                maxLength={10}
                onChange={(e) => set({ mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              />
            </div>
            {showErrors && !isValidMobile(block.mobile) && (
              <div className="invalid-feedback d-block">Enter a valid 10-digit mobile number.</div>
            )}
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Operator</label>
              <select
                className={`form-select ${showErrors && !block.operator ? 'is-invalid' : ''}`}
                value={block.operator}
                onChange={(e) => set({ operator: e.target.value })}
              >
                <option value="">-- Select operator --</option>
                {operators.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Circle</label>
              <select
                className={`form-select ${showErrors && !block.circle ? 'is-invalid' : ''}`}
                value={block.circle}
                onChange={(e) => set({ circle: e.target.value })}
              >
                <option value="">-- Select circle --</option>
                {circles.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}

      {block.subType === 'BILL_PAYMENT' && (
        <>
          <div className="mb-3">
            <label className="form-label">Category</label>
            <select
              className={`form-select ${showErrors && !block.billCategory ? 'is-invalid' : ''}`}
              value={block.billCategory}
              onChange={(e) => set({ billCategory: e.target.value, biller: '' })}
            >
              <option value="">-- Select category --</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Biller</label>
            <button
              type="button"
              className={`form-control text-start nova-picker-trigger ${showErrors && !block.biller ? 'is-invalid' : ''}`}
              disabled={!block.billCategory}
              onClick={() => setBillerPickerOpen(true)}
            >
              {block.biller || 'Select biller'}
            </button>
          </div>
          <div className="mb-3">
            <label className="form-label">Consumer number</label>
            <input
              type="text"
              className={`form-control ${showErrors && !block.consumerNumber.trim() ? 'is-invalid' : ''}`}
              value={block.consumerNumber}
              onChange={(e) => set({ consumerNumber: e.target.value })}
            />
          </div>
          <PickerModal
            show={billerPickerOpen}
            title="Select biller"
            searchHint="Search biller"
            rows={billers.map((b) => ({ key: b, title: b }))}
            emptyText="No billers found"
            onSelect={(row) => {
              set({ biller: row.key });
              setBillerPickerOpen(false);
            }}
            onClose={() => setBillerPickerOpen(false)}
          />
        </>
      )}
    </div>
  );
};

export default MobileTransferFields;
